import type { TrainingPlan, UserProfile } from '@shared/types';
import { useTrainingPlanPersistence } from '../hooks/useTrainingPlanPersistence';
import { logger } from '../utils/logger';
import apiClient from './api';
import {
  isStoredPlanCompatible,
  loadTrainingPlan,
  saveTrainingPlan,
  updateTrainingPlan,
} from './trainingPlanStorage';

/**
 * Training Plan Manager Service
 *
 * Centralizes training plan generation logic with localStorage persistence.
 * Handles the decision logic for when to use stored plans vs generating new ones.
 */

export interface TrainingPlanGenerationOptions {
  forceNewPlan?: boolean;
  skipStorage?: boolean;
}

export interface TrainingPlanGenerationResult {
  plan: TrainingPlan;
  source: 'stored' | 'backend-new' | 'backend-updated';
  wasStored: boolean;
}

/**
 * Core training plan manager class
 */
export class TrainingPlanManager {
  private persistence: ReturnType<typeof useTrainingPlanPersistence> | null = null;

  constructor(persistence?: ReturnType<typeof useTrainingPlanPersistence>) {
    this.persistence = persistence || null;
  }

  /**
   * Generate or update a training plan with smart persistence logic
   */
  async generateOrUpdatePlan(
    userProfile: UserProfile,
    options: TrainingPlanGenerationOptions = {}
  ): Promise<TrainingPlanGenerationResult> {
    const { forceNewPlan = false, skipStorage = false } = options;

    // Check if we have a stored plan and if it's compatible
    const hasCompatibleStoredPlan =
      !skipStorage &&
      this.persistence?.hasStoredPlan &&
      this.persistence?.checkCompatibility(userProfile);

    // If forcing new plan or no compatible stored plan, generate fresh
    if (forceNewPlan || !hasCompatibleStoredPlan) {
      logger.info('Generating new training plan from backend');

      const response = await apiClient.generateTrainingPlan(userProfile, userProfile.planStartDate);

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to generate training plan');
      }

      // Save to storage if not skipping (syncs to backend)
      if (!skipStorage && this.persistence) {
        await this.persistence.saveToStorage(response.data, userProfile);
      }

      return {
        plan: response.data,
        source: 'backend-new',
        wasStored: false,
      };
    }

    // We have a compatible stored plan - send it to backend for potential updates
    logger.info('Updating existing training plan via backend');

    const existingPlan = this.persistence!.storedPlan!;
    const response = await apiClient.generateOrUpdateTrainingPlan(userProfile, existingPlan);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to update training plan');
    }

    // Update storage with the response (syncs to backend)
    if (!skipStorage && this.persistence) {
      await this.persistence.updateInStorage(response.data, userProfile);
    }

    return {
      plan: response.data,
      source: 'backend-updated',
      wasStored: true,
    };
  }

  /**
   * Load training plan with fallback logic
   */
  async loadTrainingPlan(userProfile: UserProfile): Promise<TrainingPlanGenerationResult | null> {
    if (!this.persistence?.hasStoredPlan) {
      return null;
    }

    // Check compatibility
    if (!this.persistence.checkCompatibility(userProfile)) {
      logger.warn('Stored plan incompatible with current profile, clearing');
      this.persistence.clearStorage();
      return null;
    }

    const storedPlan = this.persistence.storedPlan;
    if (!storedPlan) {
      return null;
    }

    return {
      plan: storedPlan,
      source: 'stored',
      wasStored: true,
    };
  }

  /**
   * Clear stored training plan
   */
  clearStoredPlan(): void {
    if (this.persistence) {
      this.persistence.clearStorage();
    }
  }

  /**
   * Get storage information
   */
  getStorageInfo() {
    return (
      this.persistence?.storageInfo || {
        hasStoredPlan: false,
        storageAvailable: false,
      }
    );
  }
}

/**
 * Factory function to create a training plan manager with persistence
 */
export function createTrainingPlanManager(
  persistence: ReturnType<typeof useTrainingPlanPersistence>
): TrainingPlanManager {
  return new TrainingPlanManager(persistence);
}

/**
 * Standalone functions for use without the class (useful for testing)
 */

/**
 * Simple function to generate training plan with persistence
 */
export async function generateTrainingPlanWithPersistence(
  userProfile: UserProfile,
  options: TrainingPlanGenerationOptions = {}
): Promise<TrainingPlan> {
  // For standalone use, we'll use direct storage functions
  const { forceNewPlan = false, skipStorage = false } = options;

  if (!skipStorage && !forceNewPlan) {
    // Try to load from storage first (loads from backend)
    const storedPlan = await loadTrainingPlan();
    if (storedPlan && isStoredPlanCompatible(userProfile)) {
      logger.info('Using stored training plan');

      // Send to backend for potential updates
      const response = await apiClient.generateOrUpdateTrainingPlan(userProfile, storedPlan);

      if (response.success && response.data) {
        // Update storage (syncs to backend)
        await updateTrainingPlan(response.data, userProfile);
        return response.data;
      }
    }
  }

  // Generate new plan
  logger.info('Generating new training plan');
  const response = await apiClient.generateTrainingPlan(userProfile, userProfile.planStartDate);

  if (!response.success || !response.data) {
    throw new Error(response.message || 'Failed to generate training plan');
  }

  // Save to storage (syncs to backend)
  if (!skipStorage) {
    await saveTrainingPlan(response.data, userProfile);
  }

  return response.data;
}
